import { axios } from "../../lib/axios";
import envConfig from "../../environment";

export type ValidationIssue = {
  field: string;
  code: string;
  message: string;
};

export type OnboardingUrlData = {
  signupUrl: string;
  instructions: string[];
};

export type OnboardingUrlResponse = {
  statusCode: number;
  data: OnboardingUrlData;
  message: string;
};

export type UploadSuccessData = {
  creditReportId: string;
  creditScore: number;
  grade: string;
  validationStatus: "valid";
  isCreditAssessment: boolean;
  utilization?: {
    totalCreditLimit: number;
    totalBalance: number;
    utilizationPercent: number;
  };
  loans?: unknown[];
  payments?: unknown[];
  summary?: {
    totalActiveLoans: number;
    totalOutstanding: number;
    missedPaymentsCount: number;
    onTimePaymentRate: number;
  };
  bureauScores?: {
    experian: number;
    equifax: number;
    transUnion: number;
  };
};

export type UploadSuccessResponse = {
  statusCode: number;
  data: UploadSuccessData;
  message: string;
};

export type UploadValidationErrorData = {
  creditReportId: string;
  validationStatus: "rejected" | "needs_review";
  validationErrors: ValidationIssue[];
  validationWarnings: ValidationIssue[];
};

export type UploadValidationErrorResponse = {
  success: false;
  message: string;
  data: UploadValidationErrorData;
};

export type LatestReport = {
  uuid: string;
  creditScore: number | null;
  validationStatus: string;
  validationErrors?: ValidationIssue[];
  validationWarnings?: ValidationIssue[];
  createdAt: string;
};

export type CreditAssessmentStatusData = {
  userId: string;
  isCreditAssessment: boolean;
  creditScore: number | null;
  latestReport?: LatestReport;
};

export type CreditAssessmentStatusResponse = {
  statusCode: number;
  data: CreditAssessmentStatusData;
  message?: string;
};

export type CreditReportLoan = {
  uuid?: string;
  lenderName?: string;
  accountType?: string;
  originalAmount?: number | string | null;
  outstandingBalance?: number | string | null;
  creditLimit?: number | string | null;
  interestRate?: number | string | null;
  emiAmount?: number | string | null;
  status?: string;
  openedDate?: string | null;
};

export type CreditReportPayment = {
  uuid?: string;
  accountRef?: string;
  dueDate?: string | null;
  paidDate?: string | null;
  amount?: number | string | null;
  status?: string;
};

export type CreditReportProfile = {
  uuid: string;
  creditScore: number | null;
  grade: string | null;
  reportDate?: string | null;
  totalCreditLimit?: number | string | null;
  totalBalance?: number | string | null;
  utilizationPercent?: number | string | null;
  validationStatus?: string;
  validationWarnings?: ValidationIssue[];
  source?: string;
  ocrConfidence?: number | string | null;
  uploadedAt?: string;
  bureauScores?: {
    experian?: number | null;
    equifax?: number | null;
    transUnion?: number | null;
  } | null;
  summary?: {
    totalActiveLoans?: number;
    totalOutstanding?: number;
    missedPaymentsCount?: number;
    onTimePaymentRate?: number;
  } | null;
  utilization?: {
    totalCreditLimit?: number;
    totalBalance?: number;
    utilizationPercent?: number;
  } | null;
  loans?: CreditReportLoan[];
  payments?: CreditReportPayment[];
};

export type LatestCreditReportData = {
  userId: string;
  isCreditAssessment: boolean;
  creditScore: number | null;
  report: CreditReportProfile | null;
};

export type LatestCreditReportResponse = {
  statusCode: number;
  data: LatestCreditReportData;
  message?: string;
};

export type CreditReportDetailResponse = {
  statusCode: number;
  data: CreditReportProfile;
  message?: string;
};

export const getOnboardingUrl = (): Promise<OnboardingUrlResponse> => {
  return axios.get(`${envConfig.apiUrl}/credit-assessment/onboarding-url`, {
    timeout: 15000,
  });
};

export const uploadCreditReport = (
  userId: string,
  file: File
): Promise<UploadSuccessResponse> => {
  const formData = new FormData();
  formData.append("userId", userId);
  formData.append("creditReport", file);

  return axios.post(`${envConfig.apiUrl}/credit-assessment/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 60000,
  });
};

export const getCreditAssessmentStatus = (
  userId: string
): Promise<CreditAssessmentStatusResponse> => {
  return axios.get(
    `${envConfig.apiUrl}/credit-assessment/status/${userId}`,
    { timeout: 15000 }
  );
};

export const getCreditReport = (
  reportId: string
): Promise<CreditReportDetailResponse> => {
  return axios.get(
    `${envConfig.apiUrl}/credit-assessment/report/${reportId}`,
    { timeout: 15000 }
  );
};

export const getLatestCreditReport = (
  userId: string
): Promise<LatestCreditReportResponse> => {
  return axios.get(
    `${envConfig.apiUrl}/credit-assessment/report/user/${userId}`,
    { timeout: 15000 }
  );
};
